import axios from 'axios';
import { getAuthToken } from './authUtils';
import { Workflow } from '../types/workflowTypes';

// Einfache Basis-URL aus der Umgebungsvariable
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  }
});

// Füge den Auth-Token zu jeder Anfrage hinzu
api.interceptors.request.use(async (config) => {
  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Antworttyp für die Zahlungsregistrierung
 * Wird für die Abwärtskompatibilität beibehalten
 */
export interface PaymentResponse {
  success: boolean;
  message?: string;
  workflowId?: string;
  newStatus?: string;
  paymentDetails?: {
    type: 'full' | 'partial';
    amount: number;
    currency: string;
  };
}

/**
 * Lädt alle Workflows mit optionalen Filtern
 * Verwendet den GET /workflows Endpunkt
 */
export const getWorkflows = async (userId?: string | null, params?: Record<string, any>) => {
  try {
    // Wenn der erste Parameter ein Objekt ist (alter Code), behandle ihn als params
    let apiParams = params;
    if (userId && typeof userId === 'object') {
      apiParams = userId as any;
      userId = null;
    }
    
    // Verwende den korrekten Pfad mit Präfix
    const response = await api.get('/workflows', { params: apiParams });
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[API] getWorkflows Antwort:', response.data);
    }
    
    if (response.data && response.data.success && Array.isArray(response.data.data)) {
      return response.data.data;
    } else if (response.data && Array.isArray(response.data)) {
      return response.data;
    }
    
    console.error('Unerwartetes Antwortformat vom Server:', response.data);
    return [];
  } catch (error) {
    console.error('Fehler beim Laden der Workflows:', error);
    throw error;
  }
};

/**
 * Lädt einen einzelnen Workflow anhand seiner ID
 * Verwendet den GET /workflows/{workflowId} Endpunkt
 */
export const getWorkflow = async (workflowId: string) => {
  try {
    const response = await api.get(`/workflows/${workflowId}`);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] getWorkflow(${workflowId}) Antwort:`, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Fehler beim Laden des Workflows ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Erstellt einen neuen Workflow
 * Unterstützt sowohl neue als auch alte Parameter zur Abwärtskompatibilität
 */
export const createWorkflow = async (
  nameOrCustomerInfo: string | {
    id?: string;
    companyName?: string;
    name?: string;
    email?: string;
    phone?: string;
    street?: string;
    zip?: string;
    city?: string;
    country?: string;
  },
  typeOrStatus: string = 'draft',
  descriptionOrMetadata?: string | Record<string, any>,
  initialStatus?: string,
  configuration?: Record<string, any>
) => {
  try {
    // Prüfe, ob die alte oder neue API-Signatur verwendet wird
    let requestData: Record<string, any>;
    
    if (typeof nameOrCustomerInfo === 'string') {
      // Neue API-Signatur: createWorkflow(name, type, ...)
      requestData = {
        name: nameOrCustomerInfo,
        type: typeOrStatus,
        description: typeof descriptionOrMetadata === 'string' ? descriptionOrMetadata : undefined,
        initialStatus,
        configuration
      };
      
      // Füge Metadaten hinzu, wenn sie vorhanden sind
      if (typeof descriptionOrMetadata === 'object') {
        requestData.metadata = descriptionOrMetadata;
      }
    } else {
      // Alte API-Signatur: createWorkflow(customerInfo, status, metadata)
      requestData = {
        customerInfo: nameOrCustomerInfo,
        status: typeOrStatus,
        metadata: descriptionOrMetadata as Record<string, any>
      };
    }
    
    const response = await api.post('/workflows', requestData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log('[API] createWorkflow Antwort:', response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error('Fehler beim Erstellen des Workflows:', error);
    throw error;
  }
};

/**
 * Aktualisiert einen bestehenden Workflow
 * Verwendet den PUT /workflows/{workflowId} Endpunkt
 */
export const updateWorkflow = async (
  workflowId: string,
  updateData: Partial<Workflow>
) => {
  try {
    const response = await api.put(`/workflows/${workflowId}`, updateData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] updateWorkflow(${workflowId}) Antwort:`, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Fehler beim Aktualisieren des Workflows ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Aktualisiert den Status eines Workflows
 * Unterstützt sowohl neue als auch alte Parameter zur Abwärtskompatibilität
 */
export const updateWorkflowStatus = async (
  workflowId: string,
  newStatus: string,
  notesOrDetails?: string | Record<string, any>
) => {
  try {
    let statusData: Record<string, any>;
    
    if (typeof notesOrDetails === 'string') {
      // Neue API-Signatur mit notes als string
      statusData = { 
        status: newStatus, 
        notes: notesOrDetails 
      };
    } else {
      // Alte API-Signatur mit details als object
      statusData = { 
        status: newStatus, 
        details: notesOrDetails
      };
    }
    
    const response = await api.put(`/workflows/${workflowId}/status`, statusData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] updateWorkflowStatus(${workflowId}) Antwort:`, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Fehler beim Aktualisieren des Workflow-Status ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Fügt ein Dokument zu einem Workflow hinzu
 * Unterstützt sowohl neue als auch alte Parameter zur Abwärtskompatibilität
 */
export const addDocumentToWorkflow = async (
  workflowId: string,
  documentData: {
    // Neue API-Parameter
    fileName?: string;
    fileContent?: string;
    documentType?: string;
    description?: string;
    metadata?: Record<string, any>;
    
    // Alte API-Parameter
    id?: string;
    type?: 'invoice' | 'offer';
    number?: string;
    date?: string;
    status?: string;
    amount?: number;
    currency?: string;
  }
) => {
  try {
    // Prüfe, ob die alten oder neuen Parameter verwendet werden
    let payload: Record<string, any>;
    
    if (documentData.fileName || documentData.fileContent) {
      // Neue API-Parameter werden verwendet
      payload = documentData;
    } else {
      // Alte API-Parameter werden verwendet - konvertiere zu neuem Format
      payload = {
        fileName: `document_${documentData.id || Date.now()}.json`,
        fileContent: JSON.stringify(documentData),
        documentType: documentData.type || 'unknown',
        description: `${documentData.type || 'Dokument'} ${documentData.number || ''}`,
        metadata: {
          id: documentData.id,
          type: documentData.type,
          number: documentData.number,
          date: documentData.date,
          status: documentData.status,
          amount: documentData.amount,
          currency: documentData.currency
        }
      };
    }
    
    const response = await api.post(`/workflows/${workflowId}/documents`, payload);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] addDocumentToWorkflow(${workflowId}) Antwort:`, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Fehler beim Hinzufügen des Dokuments zum Workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Registriert eine Zahlung für einen Workflow
 * Unterstützt sowohl neue als auch alte Parameter zur Abwärtskompatibilität
 */
export const registerPayment = async (
  workflowId: string,
  amountOrNewStatus: number | string,
  currencyOrPaymentDetails: string | {
    documentId?: string;
    documentNumber?: string;
    amount: number;
    currency: string;
    paymentDate: string;
    paymentType: 'full' | 'partial';
    paymentMethod?: string;
    note?: string;
  },
  paymentDateOrComment?: string,
  paymentMethod?: string,
  invoiceId?: string,
  note?: string
): Promise<PaymentResponse> => {
  try {
    let paymentData: Record<string, any>;
    
    if (typeof amountOrNewStatus === 'number') {
      // Neue API-Signatur: registerPayment(workflowId, amount, currency, paymentDate, ...)
      paymentData = {
        workflowId,
        amount: amountOrNewStatus,
        currency: currencyOrPaymentDetails as string,
        paymentDate: paymentDateOrComment,
        paymentMethod,
        invoiceId,
        notes: note
      };
    } else {
      // Alte API-Signatur: registerPayment(workflowId, newStatus, paymentDetails, comment)
      paymentData = {
        workflowId,
        newStatus: amountOrNewStatus,
        paymentDetails: currencyOrPaymentDetails,
        comment: paymentDateOrComment
      };
    }
    
    const response = await api.post('/workflows/payment/register', paymentData);
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] registerPayment(${workflowId}) Antwort:`, response.data);
    }
    
    // Stellen sicher, dass die Antwort im alten PaymentResponse-Format zurückgegeben wird
    const result: PaymentResponse = {
      success: response.data.success || true,
      message: response.data.message || 'Zahlung erfolgreich registriert',
      workflowId: response.data.workflowId || workflowId,
      newStatus: response.data.newStatus,
      paymentDetails: response.data.paymentDetails
    };
    
    return result;
  } catch (error) {
    console.error(`Fehler beim Registrieren der Zahlung für Workflow ${workflowId}:`, error);
    throw error;
  }
};

/**
 * Aktualisiert den Titel eines Workflows
 * @param workflowId - ID des Workflows
 * @param name - Neuer Titel
 * @returns Aktualisierter Workflow
 */
export const updateWorkflowTitle = async (workflowId: string, name: string) => {
  try {
    const response = await api.put(`/workflows/${workflowId}/title`, { name });
    
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API] updateWorkflowTitle(${workflowId}) Antwort:`, response.data);
    }
    
    return response.data;
  } catch (error) {
    console.error(`Fehler beim Aktualisieren des Workflow-Titels ${workflowId}:`, error);
    throw error;
  }
};